import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, OnDestroy, OnInit, ViewChild, input, inject } from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { AbstractControlValueAccessor } from '@app/shared/abstract_control_value_accessor.abstract';
import { SchemaTranslations } from '@app/shared/models/schema-translations';
import { Schema } from '@app/core/_models/schema';
import { Subscription } from 'rxjs';
import { isMobileOrTablet, isNativeDatepickerSupported } from '../../../../shared/utils/browser-checks';
import { format, isValid } from 'date-fns';

import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { AppFocusableDirective } from '@app/shared/directives/focusable.directive';

const isValidDate = (date: string | Date, nativeDatepicker: boolean): boolean =>
  nativeDatepicker ? isValid(new Date(date)) : isValid(date);

@Component({
  selector: 'app-property-date',
  templateUrl: './property-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertyDateComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => PropertyDateComponent), multi: true },
  ],
  imports: [
    BsDatepickerModule,
    AppFocusableDirective,
    ReactiveFormsModule
],
})
export class PropertyDateComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy, Validator {
  private cdr = inject(ChangeDetectorRef);

  public readonly NATIVE_DATEPICKER = isNativeDatepickerSupported() && isMobileOrTablet();
  public readonly AUTOCOMPLETE = { birthday: 'bday' };
  @ViewChild('input') public input: ElementRef;
  public readonly controlName = input<string>(undefined!);
  public readonly properties = input<Schema>(undefined!);
  public readonly schemaTranslations = input<SchemaTranslations | null>(undefined!);
  public readonly errors = input<any>();
  public formControl = new UntypedFormControl(null);
  private formSub: Subscription;

  public ngOnInit(): void {
    this.formSub = this.formControl.valueChanges.subscribe(() => this.formControlChange());
  }

  public ngOnDestroy(): void {
    this.formSub.unsubscribe();
  }

  public writeValue(date: string): void {
    if (!date) { return; }

    this.formControl.patchValue(this.fillFieldWithCorrectDate(new Date(date)));
    this.cdr.markForCheck();
  }

  public validate(): ValidationErrors | any {
    return this.formControl.errors;
  }

  public onFocusChange(datepicker): void {
    if (!datepicker.isOpen) {
      datepicker.show();
    }
  }

  private formControlChange(): void {
    this.onTouched();
    this.onChange(this.parsedInputDateForBackend());
  }

  private fillFieldWithCorrectDate(date: Date): Date | string | null {
    if (isValidDate(date, this.NATIVE_DATEPICKER)) {
      return this.NATIVE_DATEPICKER ? format(date, 'yyyy-MM-dd') : date;
    } else {
      return null;
    }
  }

  private parsedInputDateForBackend(): string | null {
    if (isValidDate(this.formControl.value, this.NATIVE_DATEPICKER)) {
      return this.NATIVE_DATEPICKER ? this.formControl.value : format(this.formControl.value, 'yyyy-MM-dd');
    } else {
      return null;
    }
  }
}
